import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/js/plugins.pkgd.min.js";
import FroalaEditorCore from "froala-editor";
import { toast } from "react-toastify";
import "./index.scss";
import { transformEditorText } from "../../../../utils/helpers";
import { colors } from "../../../../utils";
import ContentEditable from "react-contenteditable";
import {
  applyHeadingProps,
  applyParagraphProps,
  getCharacterCount,
  enforceCharLimit,
  removeInlineHeadingColors,
  removeInlineParagraphColors,
  stripInlineTextAlign,
  plainTextToParagraphHtml,
} from "../FroalaHelpers";
import { makeToolbarKeyboardReachable } from "./toolbarKeyboardAccess";
import { convertMarkdownEmphasisToHtml } from "./markdownEmphasisToHtml";
import { buildRichPastePayload } from "./richPastePayload";
import { toResponsiveToolbarConfig } from "./toolbarResponsiveConfig";
import {
  saveEditorSelection,
  restoreEditorSelectionForCommand,
} from "./ensureToolbarCommandSelection";
import { getSelectedSegmentCharCount } from "../../../../redux/selectors/segmentBuilderSelectors";
import { charLimitInASegment } from "../../../SegmentSettings";
import EmojiPicker from "emoji-picker-react";
import { Box } from "@mui/material";
import { Close } from "@mui/icons-material";
// ISP-0000: the emoji button's geometry contract (24px icon grid, un-clipped
// icon) lives in its own module so it can be asserted without a browser.
import {
  buildEmojiToolbarIconMarkup,
  EMOJI_TOOLBAR_BUTTON_CSS,
} from "./emojiToolbarButton";
// gordon-0915-froala-toolbar-kebab-ur-emoji-clipped-bullet-missing: pins the
// "more" (kebab) toggle to the toolbar's upper-right corner. See the module
// for why the existing `.fr-btn-grp:nth-child(2)` CSS never did this.
import { TOOLBAR_KEBAB_POSITION_CSS } from "./toolbarKebabPosition";
// T22.187: drag/keyboard grip that grows a fixed-height editor (Description).
import EditorResizeGrip from "./editorResizeGrip";
// T22.456: closure-free toolbar commands must exist before ANY editor mounts.
import { registerStaticToolbarCommands } from "./registerStaticToolbarCommands";

registerStaticToolbarCommands(FroalaEditorCore);

const FroalaTextEditor = ({
  name,
  value: externalValue,
  id,
  onBlur,
  onChange,
  disabled,
  handleEditorCustomization = () => {},
  editorRef,
  editorHeight,
  placeholder = "Hello there!",
  customDefaultFontSize,
  trimUrls = false,
  toolbarButtons = [
    "textColorCustom",
    "paragraphFormat",
    "fontFamily",
    "fontSize",
    "customBackgroundColor",
    "bold",
    "italic",
    "underline",
    "alignLeft",
    "alignCenter",
    "alignRight",
    "|",
    "simpleUL", // Regular bullet button
    "bulletStyles", // Bullet styles dropdown
    "formatOL",
    "indent",
    "outdent",

    // "formatUL",

    "insertLink",
    "emoticons",
    "specialCharacters",
    "undo",
    "redo",
    "aiAssistant",
  ],
  label = null,
  dynamicLabelName = null,
  type = "description",
  characters = null,
  singleElement = false,
  focused = false,
  lineHeight = null,
  headingProps = null,
  paragraphProps = null,
  isExpandable = false,
  disableAtCharLimit = false,
  popupPositionMode = "default", // "default" | "relative"
  dataEditorId = null,
  autofocus = true,
  forceTextAlignLeft = false,
  allowRichPaste = false,
  resizable = false,
}) => {
  const normalizeEditorValue = (html) => {
    if (html === undefined || html === null) return "";
    let nextValue = characters ? enforceCharLimit(html, characters) : html;
    if (forceTextAlignLeft) {
      nextValue = stripInlineTextAlign(nextValue);
    }
    return nextValue;
  };

  const [value, setValue] = useState(() => normalizeEditorValue(externalValue));
  const [isInteracting, setIsInteracting] = useState(false);
  const [dynamicLabel, setDynamicLabel] = useState(dynamicLabelName);
  const [forceUpdateCounter, forceUpdate] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // stgfix-1008-B42: the character counter used to paint as soon as this
  // component first rendered (gated only on `characters`), independent of
  // whether the underlying react-froala-wysiwyg library had actually built
  // its toolbar/contenteditable DOM yet — so "Edit Wheel" first open showed
  // a label + populated count ("65/75") with no visible input box under it
  // for over a second. Gate the counter on real Froala readiness instead.
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);

  // Refs for emoji picker positioning and outside click detection
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);

  // Ref to store the saved selection before opening emoji picker
  const savedSelectionRef = useRef(null);

  // Ref to store which editor opened the emoji picker
  const activeEditorRef = useRef(null);

  // Get the character count from Redux state
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);
  const currentEditorContribution = getCharacterCount(value); // Get this editor's current character count
  const isAtCharLimit =
    disableAtCharLimit && selectedSegmentCharCount >= charLimitInASegment;

  // Use ref to store the current limit state for event handlers
  const isAtCharLimitRef = useRef(isAtCharLimit);

  // Use ref to store the current segment character count for event handlers
  const selectedSegmentCharCountRef = useRef(selectedSegmentCharCount);

  // Update the ref whenever isAtCharLimit changes
  useEffect(() => {
    isAtCharLimitRef.current = isAtCharLimit;
  }, [isAtCharLimit]);

  // Update the ref whenever selectedSegmentCharCount changes
  useEffect(() => {
    selectedSegmentCharCountRef.current = selectedSegmentCharCount;
  }, [selectedSegmentCharCount]);

  // Update character count ref whenever content changes
  const updateCharCountRef = (editor) => {
    if (!editor || !disableAtCharLimit) return;

    const currentContent = editor.html.get();
    const currentCharCount = getCharacterCount(currentContent);

    // Calculate the total segment count
    const otherEditorsCount =
      selectedSegmentCharCountRef.current - currentEditorContribution;
    const newTotal = otherEditorsCount + currentCharCount;

    selectedSegmentCharCountRef.current = newTotal;
  };

  console.log(
    "isAtCharLimit:",
    isAtCharLimit,
    "totalSegmentChars:",
    selectedSegmentCharCount,
    "thisEditorChars:",
    currentEditorContribution,
    "limit:",
    charLimitInASegment,
    "isAtCharLimitRef:",
    isAtCharLimitRef.current,
    "selectedSegmentCharCountRef:",
    selectedSegmentCharCountRef.current,

    "hama"
  );

  useEffect(() => {
    if (externalValue !== undefined && externalValue !== null) {
      setValue(normalizeEditorValue(externalValue));
    } else {
      setValue("");
    }
  }, [externalValue, characters, forceTextAlignLeft]);

  const getEditorText = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  // nem-builder-namefield-counter-lies-about-length: enforce the real cap here,
  // the single path every model change (typed input, paste, undo/redo, AI
  // insert) funnels through -- so the stored value can never exceed
  // `characters` regardless of how the overflow was produced.
  const handleModelChange = (newModel) => {
    const nextModel = normalizeEditorValue(newModel);
    setValue(nextModel);
    if (onChange) onChange(nextModel);
  };

  // Helper to replace non-breaking spaces with regular spaces
  const normalizeSpaces = (text) => text.replace(/&nbsp;|\u202F|\u00A0/g, " ");

  // Trim only leading/trailing whitespace (including &nbsp;) **without** removing HTML tags like <a>
  const trimHtmlSpaces = (html) => {
    if (!html) return html;
    // Remove spaces immediately inside tags boundaries and at start/end
    let cleaned = html;
    cleaned = cleaned.replace(/^((?:\s|&nbsp;|\u00A0)+)/g, ""); // leading
    cleaned = cleaned.replace(/((?:\s|&nbsp;|\u00A0)+)$/g, ""); // trailing
    cleaned = cleaned.replace(/>(?:\s|&nbsp;|\u00A0)+/g, ">"); // after '>'
    cleaned = cleaned.replace(/(?:\s|&nbsp;|\u00A0)+</g, "<"); // before '<'
    return cleaned;
  };

  // Handle outside click for emoji picker
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  // Handle emoji selection
  const handleEmojiSelect = (emojiObject) => {
    // Use the editor that actually opened the emoji picker
    const targetEditor = activeEditorRef.current || editorRef.current;

    if (targetEditor) {
      // Build emoji HTML like Froala native
      const emojiHTML = `<span class="fr-emoticon fr-deletable" data-emoji-styled="true">${emojiObject.emoji}</span>`;

      // Insert inline at restored caret position
      targetEditor.html.insert(emojiHTML, false);

      // Ensure font-inheritance attributes are applied
      preserveFontAfterEmoji(targetEditor);

      // Get the updated content and trigger onChange only
      let newContent = targetEditor.html.get();
      if (characters) {
        const cappedContent = enforceCharLimit(newContent, characters);
        if (cappedContent !== newContent) {
          targetEditor.html.set(cappedContent);
          newContent = cappedContent;
        }
      }
      setValue(newContent);
      if (onChange) {
        onChange(newContent);
      }

      // Update character count ref immediately
      updateCharCountRef(targetEditor);

      // Trigger contentChanged event to ensure all handlers are called
      setTimeout(() => {
        targetEditor.events.trigger("contentChanged");
      }, 0);

      setShowEmojiPicker(false);

      // Clear the active editor reference
      activeEditorRef.current = null;
    }
  };

  useEffect(() => {
    if (FroalaEditorCore) {
      handleEditorCustomization(FroalaEditorCore);

      // Register custom emoji picker button with modern styling
      FroalaEditorCore.DefineIcon("customEmojis", {
        template: "text",
        // ISP-2712: line-art icon (product chrome = line-art, emoji = user
        // voice) — was a raw 😊 glyph, now the app's existing line-art
        // smiley asset (matches Editor/children/SecondaryToolbar's emoji
        // button).
        // ISP-0000 round 3: the icon is now an INLINE <svg>, not an <img>, so
        // it takes the toolbar's colour token like every sibling icon does.
        // The asset itself (black-smiley.svg) is still live — SecondaryToolbar
        // renders it as an <img> — so it is deliberately not deleted; its path
        // data is mirrored in smileyIconPaths.js under a test that asserts the
        // two cannot drift. See emojiToolbarButton.js for the measured geometry.
        NAME: buildEmojiToolbarIconMarkup(),
      });

      // Add custom CSS styling for the emoji button and popup positioning
      const style = document.createElement("style");
      style.textContent = EMOJI_TOOLBAR_BUTTON_CSS;
      if (!document.querySelector("style[data-froala-emoji]")) {
        style.setAttribute("data-froala-emoji", "true");
        document.head.appendChild(style);
      }

      // gordon-0915-froala-toolbar-kebab-ur-emoji-clipped-bullet-missing: pins
      // the "more" (kebab) toggle to the toolbar's upper-right corner on every
      // editor that renders one (see toolbarKebabPosition.js).
      const kebabStyle = document.createElement("style");
      kebabStyle.textContent = TOOLBAR_KEBAB_POSITION_CSS;
      if (!document.querySelector("style[data-froala-kebab-position]")) {
        kebabStyle.setAttribute("data-froala-kebab-position", "true");
        document.head.appendChild(kebabStyle);
      }

      FroalaEditorCore.RegisterCommand("emoticons", {
        title: "Insert Emoji",
        icon: "customEmojis",
        undo: false,
        focus: false,
        callback: function () {
          // Save the current selection before opening the picker
          savedSelectionRef.current = this.selection.save();

          // Store reference to the editor that opened   the picker
          activeEditorRef.current = this;

          // Toggle emoji picker visibility
          setShowEmojiPicker((prev) => !prev);

          // Store reference to the button for positioning
          const toolbar = this.$tb[0];
          const emojiButton = toolbar.querySelector('[data-cmd="emoticons"]');
          if (emojiButton) {
            emojiButtonRef.current = emojiButton;
          }
        },
      });

      // T22.456: customFontSize, simpleUL, bulletStyles, textColorCustom,
      // customBackgroundColor and emptyToolbar are registered at module load
      // (registerStaticToolbarCommands, below the imports) so they exist
      // before the first editor builds its toolbar.
    }
  }, [handleEditorCustomization, editorRef]);

  const handleDrop = (dropEvent, editor) => {
    dropEvent.preventDefault();
    dropEvent.stopPropagation();

    if (
      dropEvent.originalEvent.dataTransfer.items &&
      dropEvent.originalEvent.dataTransfer.items[0].kind === "file"
    ) {
      return false;
    }
  };

  const handlePaste = (pasteEvent) => {
    const clipboardData = pasteEvent.clipboardData || window.clipboardData;
    if (clipboardData) {
      const items = clipboardData.items;
      if (items) {
        for (var i = 0; i < items.length; i++) {
          if (items[i].type.match("^image/") || items[i].kind === "file") {
            pasteEvent.preventDefault();
            return false;
          }
        }
      }

      // kelly-0915-description-rich-paste-loses-formatting: editors that opt
      // in (currently only the wheel Description field) keep a sanitized
      // allowlist of the pasted HTML instead of the pastePlain-stripped text
      // below, so a Google Docs paste's bold/italic/etc. survive.
      //
      // kelly-0915-editwheel-description-editor-copies-asterisks-ux (v2):
      // this branch calls preventDefault(), so Froala's native paste
      // pipeline -- and therefore the `paste.afterCleanup` hook #5570 added
      // below -- NEVER runs for this field. Measured on web-dev bundle
      // main.5e7a2989.js: the pasted markdown stayed as 6 literal asterisks.
      // The markdown conversion therefore has to live in the payload this
      // branch inserts. buildRichPastePayload() does the same sanitize /
      // paragraph-wrap as before, then converts markdown emphasis + bullets.
      if (allowRichPaste) {
        const pastedHtml = clipboardData.getData("text/html");
        const pastedText = clipboardData.getData("text/plain");
        if (pastedHtml || pastedText) {
          pasteEvent.preventDefault();
          const sanitized = buildRichPastePayload({
            html: pastedHtml,
            text: pastedText,
          });
          if (editorRef.current) {
            editorRef.current.html.insert(sanitized, true);
            const newContent = editorRef.current.html.get();
            if (onChange) {
              setTimeout(() => {
                onChange(newContent);
              }, 0);
            }
          }
          return false;
        }
      }

      const text = clipboardData.getData("text/plain");

      if (text && characters) {
        pasteEvent.preventDefault();
        if (editorRef.current) {
          const currentHtml = editorRef.current.html.get();
          // Count DECODED characters (entities like &nbsp; are 1 char, not 6)
          // so enforcement always matches the visible counter.
          const textOnlyLength = getCharacterCount(currentHtml);
          const selLength = window.getSelection().toString().length;
          const maxLength = characters;
          // account for selected text being overwritten
          const remaining = maxLength - (textOnlyLength - selLength);
          if (remaining <= 0) {
            return false;
          }
          const trimmedText = text.slice(0, remaining);
          editorRef.current.html.insert(plainTextToParagraphHtml(trimmedText), true);
          const newContent = editorRef.current.html.get();

          // Force update state/UI
          if (onChange) {
            setTimeout(() => {
              onChange(newContent);
            }, 0);
          }
        }
        return false;
      }
    }
    return true; // Continue with other types of pasting if not handled above
  };

  // Smart paste handler for segment character limit
  const handleSmartSegmentPaste = (pasteEvent) => {
    const clipboardData = pasteEvent.clipboardData || window.clipboardData;
    if (!clipboardData) return true;

    // Block image/file pasting
    const items = clipboardData.items;
    if (items) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.match("^image/") || items[i].kind === "file") {
          pasteEvent.preventDefault();
          return false;
        }
      }
    }

    const text = clipboardData.getData("text/plain");
    if (!text) return true;

    // Get real-time character count instead of relying on refs
    let currentSegmentChars;
    if (editorRef.current) {
      // Calculate the real current count by getting fresh content from the editor
      const currentContent = editorRef.current.html.get();
      const currentEditorCharCount = getCharacterCount(currentContent);
      // Calculate other editors' contribution by subtracting our initial contribution and adding current
      const otherEditorsCount =
        selectedSegmentCharCount - currentEditorContribution;
      currentSegmentChars = otherEditorsCount + currentEditorCharCount;
    } else {
      // Fallback to ref value if editor not available
      currentSegmentChars = selectedSegmentCharCountRef.current;
    }

    const remainingChars = charLimitInASegment - currentSegmentChars;

    if (remainingChars <= 0) {
      // No space left, block the paste
      toast.error(
        `Segment text limit (${charLimitInASegment} characters) has been reached.`
      );
      pasteEvent.preventDefault();
      return false;
    }

    if (text.length <= remainingChars) {
      // The entire text fits, allow normal paste
      return true;
    }

    // Text is too long, trim it to fit
    pasteEvent.preventDefault();

    if (editorRef.current) {
      const trimmedText = text.slice(0, remainingChars);
      editorRef.current.html.insert(plainTextToParagraphHtml(trimmedText), true);

      // Show notification about trimmed paste
      toast.info(
        `Text was trimmed to fit within the ${charLimitInASegment} character limit. ${
          text.length - remainingChars
        } characters were removed.`
      );

      // Force update state/UI
      if (onChange) {
        setTimeout(() => {
          const newContent = editorRef.current.html.get();
          onChange(newContent);
        }, 0);
      }
    }

    return false;
  };

  let editorClass = "froalaEditor";
  if (type === "title") {
    editorClass += " froalaEditor--variantTitle";
  } else if (type === "description") {
    editorClass += " froalaEditor--variantDescription";
  }

  if (singleElement) {
    editorClass += " froalaEditor--singleElement";
  }
  if (isInteracting) {
    editorClass += " froalaEditor--active";
  }
  if (popupPositionMode === "relative") {
    editorClass += " froalaEditor--relativePopup";
  }
  if (forceTextAlignLeft) {
    editorClass += " froalaEditor--forceTextAlignLeft";
  }

  const applyLinkStyles = (editor) => {
    const listItems = editor.el.querySelectorAll("li");
    listItems.forEach((li) => {
      // Select all 'a' elements within each 'li'
      const links = li.querySelectorAll("a");
      if (links.length) {
        // Directly check the inline style for color on the 'li'
        const liColor = li.style.color; // Gets the inline style color value
        links.forEach((link) => {
          if (liColor) {
            // If there's an inline color set, use it for each link
            console.log(liColor, "Applying color to link");
            link.style.color = liColor;
          } else {
            link.style.color = "#0000cc"; // Default blue if no inline color is set
          }
        });
      }
    });
  };

  // Updated preserveFontAfterEmoji function

  const preserveFontAfterEmoji = (editor) => {
    if (!editor) return;

    const frView = editor.el;
    if (!frView) return;

    // Apply the emoji font inheritance CSS property
    frView.style.setProperty(
      "--emoji-font-family",
      paragraphProps?.fontFamily || "'DM Sans', sans-serif"
    );

    // Find all emojis
    const emojis = frView.querySelectorAll(".fr-emoticon");

    // Set a data attribute on emojis to identify them for CSS targeting
    emojis.forEach((emoji) => {
      emoji.setAttribute("data-emoji-styled", "true");

      // Determine the parent element type
      const paragraphParent = emoji.closest("p");
      const headingParent = emoji.closest("h1, h2");

      if (paragraphParent) {
        emoji.setAttribute("data-parent-type", "paragraph");
      } else if (headingParent) {
        emoji.setAttribute("data-parent-type", "heading");
      }
    });
  };

  useEffect(() => {
    if (focused && editorRef.current) {
      editorRef.current?.events?.focus();
    }
  }, [focused]);

  useEffect(() => {
    if (editorRef.current && lineHeight) {
      const frView = editorRef.current.el;
      if (frView) {
        frView.style.setProperty("--line-height", lineHeight);
      }

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [lineHeight]);

  useEffect(() => {
    if (editorRef.current && headingProps) {
      applyHeadingProps(editorRef.current, headingProps);

      // Also call our custom function to handle emojis
      preserveFontAfterEmoji(editorRef.current);

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [headingProps]);

  useEffect(() => {
    if (editorRef.current && headingProps?.resetKey > 0) {
      removeInlineHeadingColors(editorRef.current);
    }
  }, [headingProps?.resetKey]);

  useEffect(() => {
    if (editorRef.current && paragraphProps) {
      applyParagraphProps(editorRef.current, paragraphProps);

      // Also call our custom function to handle emojis
      preserveFontAfterEmoji(editorRef.current);

      setTimeout(() => {
        editorRef.current.events.trigger("contentChanged");
      }, 0);
    }
  }, [paragraphProps]);

  useEffect(() => {
    if (editorRef.current && paragraphProps?.resetKey > 0) {
      removeInlineParagraphColors(editorRef.current);
    }
  }, [paragraphProps?.resetKey]);

  return (
    <>
      {label ? (
        <div
          className={`editorLabel ${
            isInteracting ? "editorLabel--active" : ""
          }`}
        >
          {label}
        </div>
      ) : dynamicLabelName ? (
        <ContentEditable
          html={transformEditorText(dynamicLabel)}
          disabled
          className={`editorLabel ${
            isInteracting ? "editorLabel--active" : ""
          }`}
          // style={{ marginBottom: "0px" }}
        />
      ) : null}

      <div className={editorClass} id={`froala-editor-${id}`}>
        <FroalaEditor
          tag="textarea"
          config={{
            key: "ZOD3gB7B10A5D6B2G2B2C-7SLJCKHXOSLMc2YGSGe1ZXHSa1CgC3E3G3H3B7A4E3F4B2C1==",
            // Replace fixed height with min-height using relative units
            autofocus,

            height: isExpandable
              ? "auto"
              : editorHeight
              ? editorHeight
              : "15rem",
            heightMin: isExpandable ? "4rem" : null,
            heightMax: isExpandable ? "18rem" : null,

            // Make sure the editor is responsive
            width: "100%",

            // Make toolbar responsive
            toolbarSticky: false,
            toolbarStickyOffset: 0,

            // Froala only writes an inline z-index onto .fr-box when this is
            // > 1 (see froala_editor.pkgd.min.js core init: `1<opts.zIndex &&
            // $box.css("z-index", ...)`). Any value > 1 here makes every
            // field instance's .fr-box a same-value stacking context, so
            // later sibling fields (Subtitle, Description) always paint
            // their whole box — toolbar included — above earlier ones
            // (DOM-order tiebreak), trapping this field's popups (e.g. the
            // Special Characters grid) behind the next field no matter how
            // high the popup's own z-index is. Keep this at the Froala
            // default of 1 so no inline z-index/stacking context is created.
            zIndex: 1,
            iframe: false,

            // Smaller buttons on smaller screens. gordon-0915-froala-toolbar-
            // kebab-ur-emoji-clipped-bullet-missing: a naive
            // `slice(0, 5).concat(["moreText"])` here used to land the overflow
            // toggle wherever index 5 fell (e.g. right after "italic" in the
            // shared default array) and silently dropped every button past it,
            // including the bullet list command -- see toolbarResponsiveConfig.js.
            toolbarButtonsXS:
              Array.isArray(toolbarButtons) && toolbarButtons.length === 0
                ? ["emptyToolbar"]
                : toResponsiveToolbarConfig(toolbarButtons),

            // Keep the rest of your config the same
            placeholderText: placeholder,

            charCounterCount: false,
            charLimit: characters,

            tabSpaces: 4,

            // toolbarContainer: "#customToolbarFroala",
            linkAlwaysBlank: true,
            linkInsertButtons: [],
            fontFamilySelection: true,
            fontSizeSelection: true,
            // fontFamilyDefaultSelection: "DM Sans",
            // fontSizeDefaultSelection: customDefaultFontSize || "16",
            colorsHEXInput: true,
            fontSize: [
              "8",
              "9",
              "10",
              "12",
              "14",
              "16",
              "18",
              "20",
              "24",
              "30",
              "36",
              "48",
              "60",
              "72",
              "96",
            ],
            attribution: false,
            paragraphFormatSelection: true,
            paragraphDefaultSelection: "Paragraph",
            keepFormatOnDelete: true,
            pasteDeniedAttrs: ["id", "class", "style"],
            pastePlain: true,
            pasteAllowedStyleProps: [],
            pasteAllowLocalImages: false,
            lineHeights: {
              Single: "1",
              1.15: "1.15",
              1.25: "1.25",
              1.5: "1.5",
              Double: "2",
            },
            paragraphFormat: {
              N: "Paragraph",
              H1: "Heading",
            },

            fontFamily: {
              "DM Sans, sans-serif": "DM Sans",
              "Arial,Helvetica,sans-serif": "Arial",
              "Georgia,serif": "Georgia",
              "Impact,Charcoal,sans-serif": "Impact",
              "Tahoma,Geneva,sans-serif": "Tahoma",
              "'Times New Roman',Times,serif": "Times New Roman",
              "Verdana,Geneva,sans-serif": "Verdana",
              "'Poppins', sans-serif": "Poppins",
              "'Quando', serif": "Quando",
            },

            // gordon-0915-froala-toolbar-kebab-ur-emoji-clipped-bullet-missing:
            // any flat array longer than the single-row budget gets grouped into
            // Froala's own moreMisc overflow format (see toolbarResponsiveConfig.js)
            // so the "more" toggle sits in the toolbar's upper-right corner and no
            // button (bullet list included) is ever dropped or clipped off a wrapped
            // row -- the same shape the Description field's own override already
            // proved out (PR#4802/#5237).
            toolbarButtons:
              Array.isArray(toolbarButtons) && toolbarButtons.length === 0
                ? ["emptyToolbar"]
                : toResponsiveToolbarConfig(toolbarButtons),

            pluginsEnabled: [
              "align",
              "charCounter",
              "codeBeautifier",
              "colors",
              "draggable",
              // "emoticons", // Disabled default emoticons plugin
              "fontSize",
              "lineHeight",
              "link",
              "lists",
              "paragraphFormat",
              "paragraphStyle",
              "quote",
              "save",
              "url",
              "fontFamily",
              "specialCharacters",
            ],

            // Disable default emoticons since we're using custom emoji picker
            // emoticonsUseImage: false,
            imageUpload: false,
            fileUpload: false,
            imagePaste: false,

            events: {
              initialized: function () {
                editorRef.current = this;
                setIsEditorInitialized(true);
                if (focused && editorRef.current.events) {
                  editorRef.current.events.focus();
                }

                // Set data attribute for editor identification
                if (dataEditorId && this.el) {
                  this.el.setAttribute("data-editor-id", dataEditorId);
                }

                const resizeObserver = new ResizeObserver(() => {
                  this.size.refresh();
                });

                // Observe the editor container
                if (this.el && this.el.parentNode) {
                  resizeObserver.observe(this.el.parentNode);
                }
                const editor = this;

                if (headingProps) {
                  applyHeadingProps(this, headingProps);
                }
                if (paragraphProps) {
                  applyParagraphProps(this, paragraphProps);
                }

                if (lineHeight) {
                  const frView = this.el;
                  if (frView) {
                    frView.style.setProperty("--line-height", lineHeight);
                  }
                }

                // Froala renders its toolbar buttons with tabindex="-1",
                // which makes Bold/Italic/Link/Emoji/AI unreachable by keyboard
                // in every rich-text field. Repair it at the shared primitive.
                if (this.$tb && this.$tb.get) {
                  const toolbarEl = this.$tb.get(0);
                  const disposeToolbarA11y = makeToolbarKeyboardReachable(
                    toolbarEl
                  );
                  // Capture-phase: save the caret before Paragraph/Heading
                  // (and other dropdowns) steal focus. Restored in commands.before.
                  const onToolbarMouseDown = () => saveEditorSelection(editor);
                  toolbarEl.addEventListener("mousedown", onToolbarMouseDown, true);
                  editor.events.on(
                    "destroy",
                    () => {
                      disposeToolbarA11y();
                      toolbarEl.removeEventListener(
                        "mousedown",
                        onToolbarMouseDown,
                        true
                      );
                    },
                    true
                  );
                }

                // Initialize character count ref
                updateCharCountRef(this);

                if (forceTextAlignLeft) {
                  const currentContent = this.html.get();
                  const leftAligned = stripInlineTextAlign(currentContent);
                  if (leftAligned !== currentContent) {
                    this.html.set(leftAligned);
                    setValue(leftAligned);
                  }
                }

                editor.events.on(
                  "drop",
                  function (dropEvent) {
                    dropEvent.preventDefault(); // Prevents the default drop behavior
                    dropEvent.stopPropagation(); // Stops the event from propagating further
                    return false; // Tells Froala to do nothing further with this event
                  },
                  true
                );
              },
              focus: function () {
                setIsInteracting(true);
              },
              blur: function () {
                setIsInteracting(false);

                // Trim URLs immediately when user finishes editing (on blur)
                if (trimUrls) {
                  const currentContent = this.html.get();
                  const cleanedHtml = trimHtmlSpaces(currentContent);
                  const cleanText = normalizeSpaces(cleanedHtml);

                  // Only update if trimming actually changed something
                  if (cleanedHtml !== currentContent) {
                    this.html.set(cleanText);
                    setValue(cleanText);
                    if (onChange) onChange(cleanText);
                    this.selection.setAtEnd(this.$el.get(0));
                  }
                }

                if (onBlur) onBlur();

                if (forceTextAlignLeft) {
                  const currentContent = this.html.get();
                  const leftAligned = stripInlineTextAlign(currentContent);
                  if (leftAligned !== currentContent) {
                    this.html.set(leftAligned);
                    setValue(leftAligned);
                    if (onChange) onChange(leftAligned);
                  }
                }
              },
              "commands.before": function (cmd) {
                const ALIGN_COMMANDS = [
                  "align",
                  "alignLeft",
                  "alignCenter",
                  "alignRight",
                  "alignJustify",
                ];
                if (forceTextAlignLeft && ALIGN_COMMANDS.includes(cmd)) {
                  return false;
                }

                // Paragraph/Heading (and font/align) apply to selection.blocks().
                // Opening those dropdowns steals focus, so the command would
                // format an empty block and wipe the field. Restore the caret
                // saved on toolbar mousedown first.
                restoreEditorSelectionForCommand(this, cmd);
              },
              "commands.after": function (cmd, param1, param2) {
                setIsInteracting(true);

                // Check if the command was to insert an emoji
                // if (cmd === "emoticons") {
                //   // Apply proper font family to emojis after insertion
                //   preserveFontAfterEmoji(this);

                //   // Immediately update the content
                //   const newContent = this.html.get();
                //   setValue(newContent);
                //   if (onChange) {
                //     onChange(newContent);
                //   }

                //   // Trigger content changed to refresh the editor
                //   setTimeout(() => {
                //     this.events.trigger("contentChanged");
                //   }, 10);
                // }
              },

              // Add emoji-specific event handling
              "emoticons.inserted": function () {
                // Apply font preservation immediately after emoji insertion
                preserveFontAfterEmoji(this);
              },

              contentChanged: function () {
                if (isExpandable) {
                  // Automatically adjust height based on content
                  this.opts.height = "auto";
                  this.size.refresh();
                }

                // Apply link styles
                applyLinkStyles(this);

                // Apply font preservation to any emojis
                preserveFontAfterEmoji(this);

                // Update character count ref for segment limit checking
                updateCharCountRef(this);
              },
              input: function () {
                // Update character count ref on any input
                updateCharCountRef(this);
              },
              keyup: function () {
                // Update character count ref after any key release (handles deletions)
                updateCharCountRef(this);
              },
              drop: handleDrop,
              paste: function (pasteEvent) {
                // Use smart paste handler if segment character limit is enabled
                if (disableAtCharLimit) {
                  return handleSmartSegmentPaste(pasteEvent);
                }

                // Continue with the existing paste handler for individual editor limits
                return handlePaste(pasteEvent);
              },
              "paste.before": function (pasteEvent) {
                // Use smart paste handler if segment character limit is enabled
                if (disableAtCharLimit) {
                  return handleSmartSegmentPaste(pasteEvent);
                }

                // Continue with the existing paste handler for individual editor limits
                return handlePaste(pasteEvent);
              },

              // kelly-0915-editwheel-description-editor-copies-asterisks-ux:
              // fires only when Froala's own native paste pipeline actually
              // runs the cleanup step -- i.e. fields WITHOUT a `characters`
              // limit (Description), where handlePaste() above never calls
              // preventDefault(). pastePlain:true flattens paste formatting
              // to plain paragraphs with zero markdown awareness, so
              // "**bold**"/"*italic*" copied from a markdown-aware source
              // (ChatGPT's copy button, Notion, Slack, a raw README) landed
              // as inert literal asterisks. Converts them to real
              // <strong>/<em> after Froala's own cleanup, text-node only.
              "paste.afterCleanup": function (cleanedHtml) {
                return convertMarkdownEmphasisToHtml(cleanedHtml);
              },

              keydown: function (e) {
                // Real-time character limit check if segment limit is enabled
                if (disableAtCharLimit) {
                  // Allow navigation keys, selection keys, and keyboard shortcuts
                  const allowedKeys = [
                    "ArrowUp",
                    "ArrowDown",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                    "PageUp",
                    "PageDown",
                    "Tab",
                    "Escape",
                    "F1",
                    "F2",
                    "F3",
                    "F4",
                    "F5",
                    "F6",
                    "F7",
                    "F8",
                    "F9",
                    "F10",
                    "F11",
                    "F12",
                  ];

                  // Allow selection and deletion operations
                  const isSelectionKey =
                    e.shiftKey &&
                    [
                      "ArrowUp",
                      "ArrowDown",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                      "PageUp",
                      "PageDown",
                    ].includes(e.key);
                  const isDeletionKey = ["Backspace", "Delete"].includes(e.key);
                  const isShortcutKey = e.ctrlKey || e.metaKey;

                  // Check if this is a character that would add content
                  const isCharacterKey =
                    e.key.length === 1 && !isShortcutKey && !e.altKey;

                  if (isCharacterKey) {
                    // Get current content from the editor
                    const currentContent = this.html.get();
                    const currentCharCount = getCharacterCount(currentContent);

                    // Get the selected text length (in case of replacement)
                    const selectedTextLength = this.selection.text().length;

                    // Calculate other editors' contribution
                    // Use the initial contribution and current count to get accurate total
                    const otherEditorsCount =
                      selectedSegmentCharCount - currentEditorContribution;

                    // Calculate what the new total would be after this character
                    // Account for selected text that will be replaced
                    const projectedTotal =
                      otherEditorsCount +
                      currentCharCount +
                      1 -
                      selectedTextLength;

                    // Check if adding this character would exceed the limit
                    if (projectedTotal > charLimitInASegment) {
                      // Show toast notification when trying to type at limit
                      toast.error(
                        `Segment text limit (${charLimitInASegment} characters) has been reached.`
                      );
                      e.preventDefault();
                      return false;
                    }
                  }

                  // Block typing new characters when at limit unless it's allowed
                  if (
                    isAtCharLimitRef.current &&
                    !allowedKeys.includes(e.key) &&
                    !isSelectionKey &&
                    !isDeletionKey &&
                    !isShortcutKey
                  ) {
                    // Show toast notification when trying to type at limit
                    toast.error(
                      `Segment text limit (${charLimitInASegment} characters) has been reached.`
                    );
                    e.preventDefault();
                    return false;
                  }
                }

                // Existing character limit code for individual editors
                const editor = this;
                const max = editor.opts.charLimit;
                if (!max) return;
                // Count DECODED characters (entities like &nbsp; are 1 char,
                // not 6) so enforcement always matches the visible counter.
                const plainLength = getCharacterCount(editor.html.get());
                const sel = window.getSelection().toString();
                if (
                  e.key.length === 1 &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  !e.altKey
                ) {
                  const projected = plainLength - sel.length + 1;
                  if (projected > max) {
                    e.preventDefault();
                    return false;
                  }
                }
                // only printable keys
              },
            },
          }}
          model={value}
          onModelChange={handleModelChange}
          disabled={disabled}
          data-editor-id={dataEditorId}
        />
        {resizable && (
          <EditorResizeGrip
            editorRef={editorRef}
            minPx={parseFloat(editorHeight) || 40}
            label={`Resize ${label || "text"} box`}
          />
        )}
        {characters && isEditorInitialized && (
          <div
            className="character-counter"
            role="status"
            aria-live="polite"
            style={{
              textAlign: "right",
              fontSize: "12px",
              // #999 on white measured 2.84:1 live via axe-core, below the 4.5:1
              // AA floor. textGrey (#6B6F80, 4.99:1 on white) is the app's
              // existing AA-passing muted-text token, used here in place of it.
              color: getCharacterCount(value) >= characters ? "#ff4d4f" : colors.textGrey,
              margin: "5px 0 0px 0",
            }}
          >
            {`${getCharacterCount(value)}/${characters}`}
          </div>
        )}

        {/* Custom Emoji Picker */}
        {showEmojiPicker && (
          <>
            {/* Custom CSS to fix skin tone picker overlap */}
            <style>
              {`
                .epr_q53mwh.epr_-8ygbw8.epr_346qu6.epr_-lzq3oa {
                  padding-bottom: 0 !important;
                }
                .epr-search-container {
                  position: relative !important;
                }
                .epr-icn-search {
                  position: absolute !important;
                  right: 12px !important;
                  top: 28% !important;
                  transform: translateY(-50%) !important;
                  pointer-events: none !important;
                  color: #666 !important;
                }
              `}
            </style>
            <Box
              ref={emojiPickerRef}
              style={{
                position: "fixed",
                zIndex: 9999,
                top: "50vh",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                borderRadius: "8px",
                overflow: "visible",
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                padding: "0",
              }}
            >
              <EmojiPicker
                onEmojiClick={handleEmojiSelect}
                theme="light"
                autoFocusSearch={true}
                searchPlaceholder="Search emojis..."
                width={350}
                height={400}
                // BUG-editwheel-bc2997: the emoji popup must only pick emoji,
                // not colours. emoji-picker-react's skin-tone selector renders
                // a row of skin-tone colour swatches that reads as a stray
                // "colour picker" inside the emoji popup — disable it so the
                // popup shows emoji only.
                skinTonesDisabled={true}
                previewConfig={{
                  showPreview: true,
                  defaultEmoji: "1f60a",
                  defaultCaption: "Choose your emoji!",
                }}
                searchDisabled={false}
              />
              <Box
                onClick={() => setShowEmojiPicker(false)}
                style={{
                  borderRadius: "50%",
                  cursor: "pointer",
                  position: "absolute",
                  right: 10,
                  top: 10,
                  height: "24px",
                  width: "24px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(240, 240, 240, 0.95)",
                  zIndex: 10000,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Close
                  style={{
                    fontSize: "18px",
                    color: "#555",
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </div>
    </>
  );
};

export default FroalaTextEditor;
